import { Component, OnDestroy, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgSelectComponent } from "@ng-select/ng-select";
import {
  AlertController,
  LoadingController,
  NavController,
  NavParams,
  ToastController,
} from "ionic-angular";
import * as moment from "moment-timezone";
import { PatientMedicationsService } from "providers/patient-charts/patient-medications.service";
import { debounceTime, distinctUntilChanged, take } from "rxjs/operators";
import { Subject } from "rxjs/Subject";
import ContentWrapper from "../../../../models/content-wrapper/content-wrapper";
import SortOptions from "../../../../models/content-wrapper/sort-options";
import CompanyResourceSummary from "../../../../models/resource-management/company-resource-summary";
import CompanyResourceType from "../../../../models/resource-management/company-resource-type.enum";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import {
  MedicationDiscontinued,
  PatientMedication,
} from "../patient-medication";

@Component({
  selector: "medication-discontinue",
  templateUrl: "medication-discontinue.html",
})
export default class MedicationDiscontinuePage implements OnDestroy {
  medicationForm: FormGroup;
  physicians: CompanyResourceSummary[] = [];
  physicianSearchSubject: Subject<string> = new Subject();
  medication: PatientMedication;
  spinner: any = null;
  spinnerCounter: number = 0;
  patientChartId: number = 0;
  futureDiscontinueDate: boolean = false;

  @ViewChild("orderingProviderId") orderingProviderInput: NgSelectComponent;
  @ViewChild("hiddenInput") hiddenInput: {
    setFocus: () => void;
  };

  public constructor(
    private fb: FormBuilder,
    public navCtrl: NavController,
    private navParams: NavParams,
    private alertController: AlertController,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private medicationService: PatientMedicationsService,
    private resourceManagementService: ResourceManagementService
  ) {
    this.medication = this.navParams.data.medication;
    this.patientChartId = this.navParams.data.patientChartId;
    const discontinuedDate = new Date(this.medication.discontinuedDate);
    const todaysDate = moment().startOf("day");
    this.medicationForm = new FormGroup({
      discontinueDate: this.fb.control(
        this.medication.discontinuedDate
          ? discontinuedDate.toString()
          : new Date().toDateString(),
        Validators.required
      ),
      discontinuePhysician: this.fb.control("", Validators.required),
      discontinueReason: this.fb.control("", Validators.required),
    });

    if (discontinuedDate >= todaysDate) {
      this.futureDiscontinueDate = true;
    }

    this.setupPhysicianSearch();
  }

  physicianSearchInputChanged(e: any) {
    let searchTerm = e.target.value;
    this.physicianSearchSubject.next(searchTerm);
  }

  ngOnDestroy(): void {}

  get discontinueDate(): string {
    const selectedDate = this.medicationForm.get("discontinueDate").value;
    if (selectedDate) {
      const currentDate = moment().startOf("day");
      const selectedDateTime = moment(selectedDate);

      if (
        selectedDateTime.isSame(currentDate) ||
        selectedDateTime.isBefore(currentDate)
      ) {
        return selectedDateTime.startOf("day").toISOString();
      } else {
        return selectedDateTime.endOf("day").toISOString();
      }
    }
    return null; // Return null if no date is selected
  }

  get discontinuePhysician(): CompanyResourceSummary {
    return this.physicians.find(
      (physician) =>
        physician.id ===
        parseInt(this.medicationForm.get("discontinuePhysician").value)
    );
  }

  get discontinueReason(): string {
    return this.medicationForm.get("discontinueReason").value;
  }

  save(): void {
    let discontinue: MedicationDiscontinued = {
      discontinueDate: this.discontinueDate,
      discontinueReason: this.discontinueReason,
      providerId: this.discontinuePhysician.id,
      providerDisplayName: this.discontinuePhysician.displayName,
      timezone: moment.tz.guess(),
    };

    this.increaseSpinnerCount("Saving ...");

    this.medicationService
      .discontinueMedication(
        this.patientChartId,
        this.medication.entityId,
        discontinue
      )
      .pipe(take(1))
      .subscribe(
        (x) => {
          this.decreaseSpinnerCount();
          this.navCtrl.pop();
        },
        (err) => {
          console.error("Error saving");
          console.error(err);

          this.decreaseSpinnerCount();
          this.presentToast("Error discontinuing medication");
        }
      );
  }

  cancel(): void {
    /**
     * Handles the cancel event
     */
    let alert = this.alertController.create({
      title: "Cancel Confirmation",
      message: "Are you sure you wish to cancel",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            this.navCtrl.pop();
          },
        },
      ],
    });
    alert.present().then((r) => console.log("Handled"));
  }

  setupPhysicianSearch(): void {
    this.physicianSearchSubject
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((x: string) => {
        if (!x || x.length === 0) {
          this.physicians = [];
        } else if (x.length > 2) {
          this.searchPhysicians(x);
        }
      });
  }

  searchPhysicians(search: string): void {
    this.resourceManagementService
      .getCompanyResourceSummaries(
        0,
        10,
        search,
        new SortOptions("last_name", "asc"),
        CompanyResourceType.PROVIDER
      )
      .pipe(take(1))
      .subscribe((x: ContentWrapper<CompanyResourceSummary>) => {
        this.physicians = x.content;
      });
  }

  presentToast(message) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "error",
    });

    toast.onDidDismiss(() => {});

    toast.present();
  }

  increaseSpinnerCount(message) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  // Hackish fix to show keyboard on IOS / Safari
  onClick() {
    this.hiddenInput.setFocus();
    this.orderingProviderInput.filterInput.nativeElement.focus();
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/medications/medication-discontinue/medication-discontinue.ts